import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import ErrorBanner from "../../components/ErrorBanner";
import DeleteDialog from "../../components/DeleteDialog";
import {
  useQueryGetCarrierTerminatedPlans,
  useMutateAddCarrierTerminatedPlans,
  useMutateRemoveCarrierTerminatedPlan,
} from "../../data/services/carrierService";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

// Parse one or more plan ids entered as comma / whitespace / newline separated text.
export const parsePlanIds = (raw) =>
  (raw || "")
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);

const CarrierTerminatedPlansTab = () => {
  const [planIdInput, setPlanIdInput] = React.useState("");
  const [planToRemove, setPlanToRemove] = React.useState(null);

  const { isLoading, data: terminatedPlans } = useQueryGetCarrierTerminatedPlans();
  const addHandler = useMutateAddCarrierTerminatedPlans();
  const removeHandler = useMutateRemoveCarrierTerminatedPlan();

  const rows = terminatedPlans?.data ?? [];

  const handleAdd = async () => {
    const planIds = parsePlanIds(planIdInput);
    if (planIds.length === 0) {
      return;
    }
    await addHandler.mutateAsync(planIds);
    setPlanIdInput("");
  };

  const handleRemove = async (item) => {
    await removeHandler.mutateAsync(item.planId);
    setPlanToRemove(null);
  };

  const actionCell = (props) => (
    <td>
      <Button
        themeColor="light"
        onClick={() => setPlanToRemove(props.dataItem)}
      >
        Remove
      </Button>
    </td>
  );

  return (
    <div>
      <div style={{ justifyContent: "space-between", display: "flex" }}>
        <h2 style={{ margin: "10px", marginBottom: 20 }}>
          Carrier Terminated Plans
        </h2>
      </div>

      <p style={{ margin: "0 10px 15px" }}>
        Plan IDs marked here are treated as Carrier Terminated for every client
        automatically. Terminated plans stay visible for historical comparison
        but are read-only and excluded from calculations, exports totals, and
        publishing.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "0 10px 15px" }}>
        <Input
          value={planIdInput}
          onChange={(e) => setPlanIdInput(e.value)}
          placeholder="Enter one or more plan IDs (comma or space separated)"
          style={{ width: 420 }}
          aria-label="Plan IDs to designate as carrier terminated"
        />
        <Button
          themeColor="primary"
          onClick={handleAdd}
          disabled={parsePlanIds(planIdInput).length === 0 || addHandler.isLoading}
        >
          Add
        </Button>
      </div>

      <ErrorBanner handler={addHandler} />
      <ErrorBanner handler={removeHandler} />

      <Grid data={rows} style={{ margin: "0 10px" }}>
        <Column field="planId" title="Plan ID" />
        {/* SOF-838: a raw plan id does not say what the plan is. */}
        <Column
          field="planDescription"
          title="Plan Name"
          cell={(props) => <td>{props.dataItem.planDescription ?? "-"}</td>}
        />
        <Column
          field="designatedAt"
          title="Designated At"
          cell={(props) => <td>{formatDate(props.dataItem.designatedAt)}</td>}
        />
        <Column field="designatedBy" title="Designated By" />
        <Column title="Action" cell={actionCell} sortable={false} />
      </Grid>

      {!isLoading && rows.length === 0 && (
        <p style={{ margin: "10px" }}>No carrier-terminated plans designated.</p>
      )}

      {planToRemove && (
        <DeleteDialog
          onDelete={handleRemove}
          onCancel={() => setPlanToRemove(null)}
          item={planToRemove}
        />
      )}
    </div>
  );
};

export default CarrierTerminatedPlansTab;
